import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import BackButton from '../components/BackButton';
import {
  MapPin, Calendar, Clock, Users, Tag, Star,
  DollarSign, Info, ExternalLink, CalendarX, X,
  Trophy
} from 'lucide-react';
import { API_URL as API } from '../config/api';
import { getTournamentImage } from './Tournaments';

const TIME_SLOTS = [
  '06:00 AM to 07:00 AM', '07:00 AM to 08:00 AM', '08:00 AM to 09:00 AM',
  '09:00 AM to 10:00 AM', '10:00 AM to 11:00 AM', '11:00 AM to 12:00 PM',
  '12:00 PM to 01:00 PM', '01:00 PM to 02:00 PM', '02:00 PM to 03:00 PM',
  '03:00 PM to 04:00 PM', '04:00 PM to 05:00 PM', '05:00 PM to 06:00 PM',
  '06:00 PM to 07:00 PM', '07:00 PM to 08:00 PM', '08:00 PM to 09:00 PM',
  '09:00 PM to 10:00 PM', '10:00 PM to 11:00 PM',
];

const SPORT_BG = {
  football: 'linear-gradient(135deg, #1a5c38 0%, #2d8a55 100%)',
  cricket: 'linear-gradient(135deg, #1a3a5c 0%, #2d6a8a 100%)',
  badminton: 'linear-gradient(135deg, #3a1a5c 0%, #6a2d8a 100%)',
  tennis: 'linear-gradient(135deg, #5c3a1a 0%, #8a6a2d 100%)',
  basketball: 'linear-gradient(135deg, #5c1a1a 0%, #8a3a2d 100%)',
  default: 'linear-gradient(135deg, #1a3a2d 0%, #2d5c45 100%)',
};

function getSportKey(sport) {
  return (sport || '').toLowerCase();
}

function formatDate(dateStr) {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  if (isToday) {
    return `Today, ${d.getDate()} ${d.toLocaleString('en-IN', { month: 'short' })} ${d.getFullYear()}`;
  }
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

function formatTimeSlot(slots) {
  if (!slots) return 'N/A';
  const list = Array.isArray(slots) ? slots : [slots];
  if (list.length === 0) return 'N/A';
  
  const first = list[0];
  const last = list[list.length - 1];

  const getStart = (s) => {
    if (!s) return '';
    if (s.includes(' to ')) return s.split(' to ')[0].trim();
    if (s.includes('-')) return s.split('-')[0].trim();
    return s;
  };

  const getEnd = (s) => {
    if (!s) return '';
    if (s.includes(' to ')) return s.split(' to ')[1].trim();
    if (s.includes('-')) return s.split('-')[1].trim();
    return s;
  };

  const startTime = getStart(first);
  const endTime = getEnd(last);

  if (!endTime) return startTime;
  return `${startTime} – ${endTime}`;
}

function getDuration(slots) {
  const count = Array.isArray(slots) ? slots.length : 1;
  return count === 1 ? '1 Hour' : `${count} Hours`;
}

function isToday(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  return d.toDateString() === new Date().toDateString();
}

function isSlotPassed(bookingDateStr, timeSlots, checkEnd = false) {
  if (!bookingDateStr) return true;
  const slots = Array.isArray(timeSlots) ? timeSlots : timeSlots ? [timeSlots] : [];
  if (slots.length === 0) return false;
  const targetSlot = checkEnd ? slots[slots.length - 1] : slots[0];
  if (!targetSlot) return false;
  try {
    const parts = targetSlot.split(/to|–|-/i);
    const timeStr = (checkEnd && parts.length > 1 ? parts[1] : parts[0]).trim();
    const match = timeStr.match(/(\d+)(?::(\d+))?\s*(AM|PM)?/i);
    if (!match) return false;
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2] || '0', 10);
    let modifier = match[3];
    if (!modifier) {
      if (targetSlot.toUpperCase().includes('PM')) modifier = 'PM';
      if (targetSlot.toUpperCase().includes('AM')) modifier = 'AM';
    }
    if (modifier && modifier.toUpperCase() === 'PM' && hours !== 12) hours += 12;
    if (modifier && modifier.toUpperCase() === 'AM' && hours === 12) hours = 0;
    const [year, month, day] = bookingDateStr.split('-').map(Number);
    const slotTime = new Date(year, month - 1, day, hours, minutes, 0);
    return new Date() >= slotTime;
  } catch (err) {
    console.error("Error parsing slot time:", err);
    return false;
  }
}

export default function MyBookings() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'upcoming';
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab') || location.state?.tab;
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [location]);
  
  const [rescheduleId, setRescheduleId] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [newSlot, setNewSlot] = useState('06:00 AM to 07:00 AM');
  const [qrModalBooking, setQrModalBooking] = useState(null);
  const [reviewBookingId, setReviewBookingId] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [selectedDetailsBooking, setSelectedDetailsBooking] = useState(null);
  const [tournamentRegs, setTournamentRegs] = useState([]);

  const fetchMyTournamentRegs = () => {
    if (!token) return;
    axios.get(`${API}/tournaments/my-registrations`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const regs = Array.isArray(res.data) ? res.data : [];
        setTournamentRegs(regs);
      })
      .catch(err => console.error('Failed to fetch tournament registrations:', err));
  };

  useEffect(() => {
    if (token) {
      axios.get(`${API}/bookings/mine`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          const data = Array.isArray(res.data) ? res.data : (res.data.bookings || []);
          setBookings(data);
          setLoading(false);
        })
        .catch(() => setLoading(false));

      fetchMyTournamentRegs();
    }
  }, [token]);

  const handleCancelTournamentRegistration = async (reg) => {
    const isWithin24 = reg.can_cancel_with_refund;
    const confirmMsg = isWithin24
      ? `Are you sure you want to cancel your team registration for "${reg.tournament_name}"?\n\nEligible for 100% full refund of ₹${reg.amount_paid}.`
      : `Are you sure you want to cancel your team registration for "${reg.tournament_name}"?\n\n⚠️ Notice: Cancellations made after 24 hours of booking are non-refundable.`;

    if (!window.confirm(confirmMsg)) return;

    try {
      const res = await axios.post(`${API}/tournaments/registrations/${reg._id}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(res.data.msg);
      fetchMyTournamentRegs();
    } catch (err) {
      alert(err.response?.data?.msg || 'Failed to cancel registration.');
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this booking?\n\nRefund policy:\n• Within 1 hour of booking → Base amount refunded (platform fee & GST retained by admin)\n• More than 6 hours before slot → Base amount refunded (platform fee & GST retained by admin)\n• Within 6 hours before slot → No refund')) return;
    try {
      const res = await axios.put(`${API}/bookings/cancel/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const { refundAmount, breakdown } = res.data;
      setBookings(prev => prev.map(b => b._id === id ? { ...b, status: 'cancelled', refund_amount: refundAmount } : b));
      if (refundAmount > 0) {
        const feeRetained = (breakdown?.platformFeeRetained || 0) + (breakdown?.gstRetained || 0);
        alert(
          `✅ Booking cancelled.\n\n` +
          `Refund to your wallet: ₹${refundAmount}\n` +
          `(Platform fee ₹${breakdown?.platformFeeRetained || 0} + GST ₹${breakdown?.gstRetained || 0} retained by admin)`
        );
      } else {
        alert('Booking cancelled.\nNo refund applicable — cancelled within 6 hours of slot start.\n50% of base amount will be paid to the venue owner as compensation.');
      }
    } catch {
      alert('Failed to cancel booking');
    }
  };

  const handleReschedule = async (id) => {
    try {
      const res = await axios.put(`${API}/bookings/reschedule/${id}`,
        { newDate, newTimeSlot: newSlot },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBookings(prev => prev.map(b => b._id === id ? { ...res.data, turf_id: b.turf_id } : b));
      setRescheduleId(null);
      alert('Rescheduled successfully! ✅');
    } catch (err) {
      alert(err.response?.data?.msg || 'Failed to reschedule');
    }
  };

  const handleReviewSubmit = async (bookingId, turfId) => {
    if (!reviewComment.trim()) { alert('Please write a comment.'); return; }
    setReviewSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        turf_id: turfId,
        booking_id: bookingId,
        rating: reviewRating,
        comment: reviewComment.trim(),
      }, { headers: { Authorization: `Bearer ${token}` } });
      setBookings(prev => prev.map(b =>
        b._id === bookingId ? { ...b, rating: reviewRating, review: reviewComment.trim() } : b
      ));
      setReviewBookingId(null);
      setReviewRating(5);
      setReviewComment('');
    } catch (err) {
      alert(err.response?.data?.detail || err.response?.data?.msg || 'Failed to submit review');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);

  const handleDownloadInvoice = async (bookingId, bookingNo) => {
    if (!token) return;
    setDownloadingInvoiceId(bookingId);
    try {
      const res = await axios.get(`${API}/bookings/${bookingId}/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TurfX_Tax_Invoice_${bookingNo || bookingId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download invoice:', err);
      alert('Failed to download invoice PDF. Please try again or contact support.');
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  const isBookingCompletedOrPassed = (b) => {
    if (b.status === 'completed' || b.status === 'checked-in') return true;
    if (['confirmed', 'pending', 'approved'].includes(b.status) && isSlotPassed(b.date, b.time_slots || b.time_slot, true)) {
      return true;
    }
    return false;
  };

  const upcoming = bookings.filter(b => ['confirmed', 'pending', 'approved'].includes(b.status) && !isSlotPassed(b.date, b.time_slots || b.time_slot, true));
  const completed = bookings.filter(b => isBookingCompletedOrPassed(b));
  const cancelled = bookings.filter(b => ['cancelled', 'refund_pending', 'refunded'].includes(b.status));

  const tabData = { upcoming, completed, cancelled };
  const shown = tabData[activeTab] || [];

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", minHeight: '100vh', background: '#F4F6F3' }}>

      {/* ── DARK GREEN HEADER ── */}
      <div style={{
        background: '#084734',
        padding: '3rem 2rem 2.5rem',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* grid overlay - same as home page */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          pointerEvents: 'none',
        }} />

        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ marginBottom: '1.25rem' }}>
            <BackButton variant="dark" />
          </div>
          {/* YOUR ACTIVITY label */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '0.6rem' }}>
            <div style={{ width: 28, height: 2, background: '#CEF17B' }} />
            <span style={{ color: '#CEF17B', fontSize: '0.75rem', fontWeight: 800, letterSpacing: '2px', textTransform: 'uppercase' }}>
              Your activity
            </span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '1.5rem' }}>
            <div>
              <h1 style={{ fontSize: '2.8rem', fontWeight: 900, margin: 0, lineHeight: 1.1 }}>
                <span style={{ color: 'white' }}>My </span>
                <span style={{ color: '#CEF17B' }}>Bookings</span>
              </h1>
              <p style={{ color: 'rgba(255,255,255,0.65)', marginTop: '0.5rem', fontWeight: 500, fontSize: '1rem' }}>
                Manage your upcoming and past turf experiences
              </p>
            </div>

            {/* Stats cards */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {[
                { key: 'upcoming', label: 'Upcoming', count: upcoming.length },
                { key: 'completed', label: 'Completed', count: completed.length },
                { key: 'cancelled', label: 'Cancelled', count: cancelled.length },
              ].map(s => (
                <div
                  key={s.key}
                  onClick={() => setActiveTab(s.key)}
                  style={{
                    background: activeTab === s.key ? 'rgba(206,241,123,0.15)' : 'rgba(255,255,255,0.07)',
                    border: activeTab === s.key ? '1.5px solid rgba(206,241,123,0.4)' : '1.5px solid rgba(255,255,255,0.12)',
                    borderRadius: '14px', padding: '14px 20px', textAlign: 'center',
                    cursor: 'pointer', minWidth: 85, transition: 'all 0.2s',
                  }}
                >
                  <div style={{ fontSize: '1.8rem', fontWeight: 900, color: activeTab === s.key ? '#CEF17B' : 'white', lineHeight: 1 }}>
                    {s.count}
                  </div>
                  <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '4px' }}>
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── TAB BAR ── pill style */}
      <div style={{ background: '#084734', padding: '0 2rem 1.8rem' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center',
            background: 'white', borderRadius: '999px',
            padding: '6px', gap: '4px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)', flexWrap: 'wrap'
          }}>
            {['upcoming', 'completed', 'cancelled'].map(t => {
              const counts = { upcoming: upcoming.length, completed: completed.length, cancelled: cancelled.length };
              const labels = { upcoming: 'Upcoming', completed: 'Completed', cancelled: 'Cancelled' };
              const isActive = activeTab === t;
              return (
                <button
                  key={t}
                  onClick={() => setActiveTab(t)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '10px 22px', borderRadius: '999px', border: 'none',
                    cursor: 'pointer', transition: 'all 0.2s',
                    background: isActive ? '#084734' : 'transparent',
                    color: isActive ? '#CEF17B' : '#374151',
                    fontSize: '0.92rem', fontWeight: 800,
                  }}
                >
                  {labels[t] || t}
                  <span style={{
                    background: isActive ? 'rgba(206,241,123,0.25)' : '#EEF2E6',
                    color: isActive ? '#CEF17B' : '#6b7280',
                    borderRadius: '999px',
                    minWidth: '22px', height: '22px', padding: '0 6px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.72rem', fontWeight: 900,
                  }}>
                    {counts[t]}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── BOOKING & TOURNAMENT CARDS ── */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '2rem' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: '5rem', color: '#374151', fontWeight: 700 }}>
            Loading your activity...
          </div>
        )}



        {activeTab === 'tournaments' ? (
          <div>
            {tournamentRegs.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '1.5rem' }}>
                {tournamentRegs.map(reg => (
                  <div
                    key={reg._id}
                    style={{
                      background: 'white', borderRadius: '20px', border: '1.5px solid #E5E7EB',
                      overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                      display: 'flex', flexDirection: 'column', justifyContent: 'space-between'
                    }}
                  >
                    <div>
                      {/* Banner Image */}
                      <div style={{ position: 'relative', width: '100%', height: '150px', overflow: 'hidden', background: 'linear-gradient(135deg, #084734 0%, #062C21 100%)' }}>
                        <img
                          src={getTournamentImage(reg.tournament_image_url || reg.image_url)}
                          alt={reg.tournament_name || 'Tournament'}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = 'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=800&auto=format&fit=crop&q=80';
                          }}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>

                      <div style={{ padding: '1.25rem' }}>
                        {/* Header Badges */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <span style={{
                            background: reg.payment_status === 'completed' ? '#DCEFB8' : '#FEE2E2',
                            color: reg.payment_status === 'completed' ? '#084734' : '#DC2626',
                            fontSize: '0.75rem', fontWeight: '800', padding: '4px 10px', borderRadius: '20px'
                          }}>
                            {reg.payment_status === 'completed' ? '✅ Registration Confirmed' : '❌ Registration Cancelled'}
                          </span>
                          <span style={{ fontSize: '0.8rem', fontWeight: '800', color: '#084734', background: '#F0FDF4', padding: '4px 10px', borderRadius: '20px' }}>
                            🏆 Tournament
                          </span>
                        </div>

                      <h3 style={{ fontSize: '1.3rem', fontWeight: '900', color: '#0D1F0F', marginBottom: '6px' }}>
                        {reg.tournament_name}
                      </h3>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.85rem', color: '#4B5563', marginBottom: '1.25rem' }}>
                        <div>📍 <strong>Venue:</strong> {reg.turf_name || 'Sports Arena'} ({reg.city || reg.address || 'N/A'})</div>
                        <div>🏆 <strong>Registered Team:</strong> <span style={{ color: '#084734', fontWeight: '900', fontSize: '0.95rem' }}>{reg.team_name}</span></div>
                        <div>👤 <strong>Captain:</strong> {reg.captain_name} (📞 {reg.captain_phone})</div>
                        {reg.alternate_phone && <div>📞 <strong>Alt Contact:</strong> {reg.alternate_phone}</div>}
                        <div>👥 <strong>Squad Players:</strong> {reg.player_count} Players</div>
                        <div>🗓️ <strong>Registration Date:</strong> {reg.created_at ? new Date(reg.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}</div>
                      </div>

                      {/* Payment & Refund Status Badge */}
                      <div style={{ background: '#F9FAFB', padding: '12px', borderRadius: '14px', border: '1px solid #E5E7EB', marginBottom: '1.25rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                          <span style={{ fontSize: '0.78rem', color: '#6B7280', fontWeight: '700', textTransform: 'uppercase' }}>Amount Paid</span>
                          <span style={{ fontSize: '1.1rem', fontWeight: '900', color: '#084734' }}>
                            {reg.amount_paid > 0 ? `₹${reg.amount_paid}` : 'FREE ENTRY'}
                          </span>
                        </div>

                        {reg.payment_status === 'completed' && (
                          <div style={{ fontSize: '0.75rem', fontWeight: '700', marginTop: '4px' }}>
                            {reg.can_cancel_with_refund ? (
                              <span style={{ color: '#16A34A', background: '#DCFCE7', padding: '3px 8px', borderRadius: '6px', display: 'inline-block' }}>
                                🟢 100% Refundable ({reg.hours_remaining_for_refund} hrs remaining)
                              </span>
                            ) : (
                              <span style={{ color: '#D97706', background: '#FEF3C7', padding: '3px 8px', borderRadius: '6px', display: 'inline-block' }}>
                                ⚠️ Non-refundable (Past 24 hours of booking)
                              </span>
                            )}
                          </div>
                        )}

                        {reg.payment_status === 'cancelled' && (
                          <div style={{ fontSize: '0.75rem', fontWeight: '700', color: reg.refund_status === 'refunded' ? '#16A34A' : '#DC2626', marginTop: '4px' }}>
                            {reg.refund_status === 'refunded' ? `💸 Full Refund of ₹${reg.refund_amount} Initiated` : '⚠️ Cancelled (No Refund Applied - After 24h)'}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Cancel Button */}
                    {reg.payment_status !== 'cancelled' && (
                      <button
                        onClick={() => handleCancelTournamentRegistration(reg)}
                        style={{
                          width: '100%', background: '#FEE2E2', color: '#DC2626', border: '1px solid #FCA5A5',
                          padding: '10px 16px', borderRadius: '12px', fontWeight: '800', fontSize: '0.88rem',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                        }}
                      >
                        <X size={16} /> Cancel Team Registration
                      </button>
                    )}
                  </div>
                </div>
              ))}
              </div>
            ) : (
              <div style={{ background: 'white', padding: '4rem 2rem', borderRadius: '24px', textAlign: 'center', border: '1.5px dashed #D1D5DB' }}>
                <Trophy size={48} color="#9CA3AF" style={{ marginBottom: '1rem' }} />
                <h3 style={{ fontSize: '1.2rem', fontWeight: '800', color: '#1F2937', marginBottom: '4px' }}>No Tournament Registrations</h3>
                <p style={{ color: '#6B7280', fontSize: '0.9rem', marginBottom: '1.5rem' }}>You haven't registered your team for any tournaments yet.</p>
                <button onClick={() => navigate('/tournaments')} style={{ background: '#084734', color: '#CEF17B', border: 'none', padding: '12px 24px', borderRadius: '50px', fontWeight: '800', cursor: 'pointer' }}>
                  Browse Tournaments
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            {!loading && shown.length === 0 && (
              <div style={{
                textAlign: 'center', padding: '5rem 2rem',
                background: 'white', borderRadius: '20px',
                border: '1.5px solid #EEF2E6',
              }}>
                <CalendarX size={52} style={{ margin: '0 auto 1rem', display: 'block', color: '#cbd5e1' }} />
                <p style={{ color: '#374151', fontWeight: 700, fontSize: '1.1rem', marginBottom: '1.5rem' }}>
                  No {activeTab} bookings yet.
                </p>
                {activeTab === 'upcoming' && (
                  <button
                    onClick={() => navigate('/explore')}
                    style={{
                      padding: '12px 28px', borderRadius: '12px', border: 'none',
                      background: '#084734', color: 'white', fontWeight: 800,
                      cursor: 'pointer', fontSize: '0.95rem',
                    }}
                  >
                    Explore Venues
                  </button>
                )}
              </div>
            )}

            {shown.map(b => (
              <BookingCard
                key={b._id}
                booking={b}
                activeTab={activeTab}
                rescheduleId={rescheduleId}
                newDate={newDate}
                newSlot={newSlot}
                setRescheduleId={setRescheduleId}
                setNewDate={setNewDate}
                setNewSlot={setNewSlot}
                handleCancel={handleCancel}
                setQrModalBooking={setQrModalBooking}
                setSelectedDetailsBooking={setSelectedDetailsBooking}
                reviewBookingId={reviewBookingId}
                setReviewBookingId={setReviewBookingId}
                reviewRating={reviewRating}
                setReviewRating={setReviewRating}
                reviewComment={reviewComment}
                setReviewComment={setReviewComment}
                reviewSubmitting={reviewSubmitting}
                handleDownloadInvoice={handleDownloadInvoice}
                downloadingInvoiceId={downloadingInvoiceId}
              />
            ))}
          </>
        )}
      </div>

      {/* QR Modal */}
      {qrModalBooking && (
        <QRModal booking={qrModalBooking} token={token} onClose={() => setQrModalBooking(null)} />
      )}

      {/* Booking Details Modal */}
      {selectedDetailsBooking && (
        <BookingDetailsModal
          booking={selectedDetailsBooking}
          onClose={() => setSelectedDetailsBooking(null)}
          handleDownloadInvoice={handleDownloadInvoice}
          downloadingInvoiceId={downloadingInvoiceId}
        />
      )}
    </div>
  );
}

function BookingCard({
  booking: b, activeTab, rescheduleId, newDate, newSlot,
  setRescheduleId, setNewDate, setNewSlot, handleCancel, handleReschedule,
  setQrModalBooking, setSelectedDetailsBooking,
  reviewBookingId, setReviewBookingId, reviewRating, setReviewRating,
  reviewComment, setReviewComment, handleReviewSubmit, reviewSubmitting,
  handleDownloadInvoice, downloadingInvoiceId,
}) {
  const navigate = useNavigate();
  const sportKey = getSportKey(b.turf_id?.sport);
  const bg = SPORT_BG[sportKey] || SPORT_BG.default;
  const todayBooking = isToday(b.date);
  const timeStr = formatTimeSlot(Array.isArray(b.time_slots) ? b.time_slots : b.time_slot ? [b.time_slot] : []);
  const duration = getDuration(Array.isArray(b.time_slots) ? b.time_slots : b.time_slot ? [b.time_slot] : []);

  return (
    <div style={{
      background: 'white', borderRadius: '20px', marginBottom: '1.2rem',
      border: '1.5px solid #EEF2E6', overflow: 'hidden',
      boxShadow: '0 2px 16px rgba(0,0,0,0.04)',
      display: 'flex',
    }}>
      {/* Sport thumbnail */}
      <div style={{
        width: 200, minHeight: 160, background: bg, flexShrink: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'flex-end',
        padding: '1rem',
        position: 'relative',
      }}>
        <div style={{ marginBottom: '0.5rem', lineHeight: 1 }}>
          <Trophy size={48} color="white" />
        </div>
        <div style={{
          background: 'rgba(0,0,0,0.45)', borderRadius: '8px',
          padding: '4px 12px', color: 'white',
          fontSize: '0.7rem', fontWeight: 800, letterSpacing: '1.5px',
          textTransform: 'uppercase',
        }}>
          {b.turf_id?.sport || 'Sport'}
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, padding: '1.4rem 1.6rem', display: 'flex', gap: '1rem' }}>
        {/* Left: details */}
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: '0 0 4px', fontSize: '1.2rem', fontWeight: 800, color: '#161616' }}>
            {b.turf_id?.name || 'Turf'}
          </h3>
          <p style={{ margin: '0 0 1rem', color: '#374151', fontSize: '0.88rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
            <MapPin size={13} /> {b.turf_id?.location}{b.turf_id?.city ? `, ${b.turf_id.city}` : ''}
          </p>

          {/* Info grid */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            <InfoChip icon={<Calendar size={14} />} label="Date" value={formatDate(b.date)} />
            <InfoChip icon={<Clock size={14} />} label="Time" value={timeStr} />
            <InfoChip icon={<Clock size={14} />} label="Duration" value={duration} />
            {activeTab === 'upcoming' && b.turf_id?.players && (
              <InfoChip icon={<Users size={14} />} label="Players" value={b.turf_id.players} />
            )}
            {activeTab === 'upcoming' && b.turf_id?.amenities?.length > 0 && (
              <InfoChip icon={<Tag size={14} />} label="Amenities" value={b.turf_id.amenities.slice(0, 2).join(', ')} />
            )}
            {activeTab === 'completed' && b.rating && (
              <InfoChip icon={<Star size={14} />} label="Your Rating" value={`${b.rating} / 5 ★`} />
            )}
            {activeTab === 'cancelled' && b.refund_amount && (
              <InfoChip icon={<DollarSign size={14} />} label="Refund" value={`₹${b.refund_amount} refunded`} />
            )}
            {activeTab === 'cancelled' && (
              <InfoChip icon={<Info size={14} />} label="Reason" value={b.cancel_reason || 'Cancelled by user'} />
            )}
          </div>
          {b.status === 'refunded' && (
            <div style={{
              marginTop: '14px',
              padding: '12px 16px',
              borderRadius: '12px',
              background: '#ECFDF5',
              border: '1px solid #A7F3D0',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              animation: 'fadeIn 0.3s ease-out'
            }}>
              <span style={{ fontSize: '1.2rem', display: 'flex', alignItems: 'center' }}>🛡️</span>
              <span style={{ fontSize: '0.82rem', color: '#065F46', fontWeight: '700', lineHeight: '1.4' }}>
                <strong>Official Notice:</strong> Your refund of <strong>₹{b.refund_amount || b.total_price}</strong> has been successfully processed. The amount will be credited to your source payment account within <strong>2 business days</strong>.
              </span>
            </div>
          )}
          {b.status === 'refund_pending' && (
            <div style={{
              marginTop: '14px',
              padding: '12px 16px',
              borderRadius: '12px',
              background: '#FFFBEB',
              border: '1px solid #FCD34D',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              animation: 'fadeIn 0.3s ease-out'
            }}>
              <span style={{ fontSize: '1.2rem', display: 'flex', alignItems: 'center' }}>⏳</span>
              <span style={{ fontSize: '0.82rem', color: '#92400E', fontWeight: '700', lineHeight: '1.4' }}>
                <strong>Refund Pending:</strong> Your refund request is currently being processed by the administrator. The funds will be transferred to your account shortly.
              </span>
            </div>
          )}
        </div>

        {/* Right: price + status + actions */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'space-between', minWidth: 160 }}>
          <div style={{ textAlign: 'right' }}>
            {/* Status badge */}
            {(() => {
              const todayStr = new Date().toISOString().split('T')[0];
              const tomorrowStr = (() => { const d = new Date(); d.setDate(d.getDate()+1); return d.toISOString().split('T')[0]; })();
              if (activeTab === 'upcoming' && b.date === todayStr) {
                return (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    background: '#F0FDF4', border: '1.5px solid #86efac',
                    borderRadius: '999px', padding: '4px 12px',
                    fontSize: '0.75rem', fontWeight: 800, color: '#16a34a',
                    marginBottom: '6px',
                  }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
                    TODAY
                  </div>
                );
              }
              if (activeTab === 'upcoming' && b.date === tomorrowStr) {
                return (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    background: '#FEF3C7', border: '1.5px solid #fcd34d',
                    borderRadius: '999px', padding: '4px 12px',
                    fontSize: '0.75rem', fontWeight: 800, color: '#b45309',
                    marginBottom: '6px',
                  }}>
                    TOMORROW
                  </div>
                );
              }
              return <StatusBadge status={b.status} />;
            })()}

            <div style={{ fontSize: '0.72rem', color: '#374151', fontWeight: 700, marginBottom: '4px', marginTop: '4px' }}>
              ID: #{b.booking_id || b._id?.slice(-6).toUpperCase()}
            </div>
            <div style={{ fontSize: '1.6rem', fontWeight: 900, color: '#161616' }}>
              ₹{b.total_price}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#374151', fontWeight: 600 }}>
              {activeTab === 'cancelled' ? `₹${b.refund_amount || 0} refunded` : 'paid'}
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
            {activeTab === 'upcoming' && (
              <>
                {(b.status === 'confirmed' || b.status === 'approved') && (
                  <ActionBtn onClick={() => setQrModalBooking(b)}>
                    Show QR
                  </ActionBtn>
                )}
                {todayBooking && (
                  <ActionBtn primary onClick={() => window.open(`https://maps.google.com/?q=${encodeURIComponent(b.turf_id?.name || '')}`, '_blank')}>
                    Get Directions
                  </ActionBtn>
                )}
                <ActionBtn onClick={() => setSelectedDetailsBooking(b)}>
                  View Details
                </ActionBtn>
                {(b.status === 'confirmed' || b.status === 'approved') && !isSlotPassed(b.date, b.time_slots || b.time_slot) && (
                  b.rescheduled ? (
                    <button disabled style={{
                      width: '100%', padding: '10px 14px', borderRadius: '8px',
                      border: 'none', background: '#F1F5F9', color: '#94A3B8',
                      fontWeight: '700', fontSize: '0.8rem', cursor: 'not-allowed',
                      textAlign: 'center'
                    }}>
                      Rescheduled (Limit: 1)
                    </button>
                  ) : (
                    <ActionBtn onClick={() => {
                      setRescheduleId(b._id);
                      setNewDate(b.date);
                      setNewSlot(b.time_slot || b.time_slots?.[0] || '06:00 AM to 07:00 AM');
                    }}>
                      Reschedule
                    </ActionBtn>
                  )
                )}
                {(b.status === 'confirmed' || b.status === 'approved') && !isSlotPassed(b.date, b.time_slots || b.time_slot) && (
                  <ActionBtn danger onClick={() => handleCancel(b._id)}>
                    Cancel Booking
                  </ActionBtn>
                )}
              </>
            )}
            {activeTab === 'completed' && (
              <>
                <ActionBtn onClick={() => navigate(`/checkout/${b.turf_id?._id}`)}>
                  Book Again
                </ActionBtn>
                {!b.rating && (
                  <div style={{ marginTop: '4px' }}>
                    {/* Star rating */}
                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end', marginBottom: '6px' }}>
                      {[1,2,3,4,5].map(star => (
                        <button
                          key={star}
                          onClick={() => setReviewRating(reviewBookingId === b._id ? star : (setReviewBookingId(b._id), star))}
                          style={{
                            fontSize: '1.4rem', background: 'none', border: 'none',
                            cursor: 'pointer', padding: 0, lineHeight: 1,
                            color: reviewBookingId === b._id && star <= reviewRating ? '#f59e0b' : '#d1d5db',
                          }}
                        >★</button>
                      ))}
                    </div>
                    {/* Comment box — only shown after a star is clicked */}
                    {reviewBookingId === b._id && (
                      <>
                        <textarea
                          value={reviewComment}
                          onChange={e => setReviewComment(e.target.value)}
                          placeholder="Share your experience..."
                          rows={2}
                          style={{
                            width: '100%', padding: '8px 10px', borderRadius: '8px',
                            border: '1.5px solid #EEF2E6', fontSize: '0.8rem',
                            fontWeight: 500, outline: 'none', resize: 'none',
                            boxSizing: 'border-box', lineHeight: 1.5, marginBottom: '6px',
                          }}
                        />
                        <button
                          onClick={() => handleReviewSubmit(b._id, b.turf_id?._id)}
                          disabled={reviewSubmitting}
                          style={{
                            width: '100%', padding: '8px', borderRadius: '8px', border: 'none',
                            background: '#084734', color: '#CEF17B', fontWeight: 800,
                            fontSize: '0.8rem', cursor: reviewSubmitting ? 'not-allowed' : 'pointer',
                            opacity: reviewSubmitting ? 0.7 : 1,
                          }}
                        >
                          {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
                        </button>
                      </>
                    )}
                  </div>
                )}
                {b.rating && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '4px',
                    justifyContent: 'flex-end', marginTop: '4px',
                    fontSize: '0.8rem', fontWeight: 700, color: '#f59e0b',
                  }}>
                    {'★'.repeat(b.rating)}{'☆'.repeat(5 - b.rating)}
                    <span style={{ color: '#374151', marginLeft: '4px' }}>{b.rating}/5</span>
                  </div>
                )}
              </>
            )}
            {activeTab === 'cancelled' && (
              <ActionBtn onClick={() => navigate(`/checkout/${b.turf_id?._id}`)}>
                Book Again
              </ActionBtn>
            )}
            <ActionBtn 
              onClick={() => handleDownloadInvoice(b._id || b.id || b.booking_id, b.booking_id || b._id)}
              disabled={downloadingInvoiceId === (b._id || b.id || b.booking_id)}
            >
              {downloadingInvoiceId === (b._id || b.id || b.booking_id) ? '⏳ Downloading...' : '📄 Download Invoice'}
            </ActionBtn>
          </div>
        </div>
      </div>

      {/* Reschedule panel */}
      {rescheduleId === b._id && (
        <div style={{
          borderTop: '1.5px dashed #EEF2E6', padding: '1.5rem',
          background: '#FAFCF8',
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '15px', maxWidth: 600 }}>
            <div>
              <label style={labelStyle}>New Date</label>
              <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)}
                style={inputStyle} min={new Date().toISOString().split('T')[0]} />
            </div>
            <div>
              <label style={labelStyle}>New Time Slot</label>
              <select value={newSlot} onChange={e => setNewSlot(e.target.value)} style={inputStyle}>
                {TIME_SLOTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '1.2rem' }}>
            <button onClick={() => handleReschedule(b._id)} style={{
              flex: 1, padding: '12px', borderRadius: '10px', border: 'none',
              background: '#084734', color: 'white', fontWeight: 800, cursor: 'pointer',
            }}>
              Confirm Reschedule
            </button>
            <button onClick={() => setRescheduleId(null)} style={{
              padding: '12px 20px', borderRadius: '10px',
              border: '1.5px solid #EEF2E6', background: 'white',
              fontWeight: 700, cursor: 'pointer',
            }}>
              Back
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

function InfoChip({ icon, label, value }) {
  return (
    <div style={{
      background: '#F8FAF7', border: '1px solid #EEF2E6',
      borderRadius: '10px', padding: '8px 12px',
      display: 'flex', alignItems: 'flex-start', gap: '8px',
    }}>
      <span style={{ marginTop: '1px', color: '#374151', flexShrink: 0 }}>{icon}</span>
      <div>
        <div style={{ fontSize: '0.65rem', color: '#374151', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {label}
        </div>
        <div style={{ fontSize: '0.85rem', fontWeight: 800, color: '#161616', marginTop: '1px' }}>
          {value}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    confirmed:        { bg: '#F0FDF4', color: '#16a34a', border: '#86efac', label: 'Confirmed' },
    pending:          { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa', label: 'Pending' },
    approved:         { bg: '#eff6ff', color: '#1e40af', border: '#bfdbfe', label: 'Upcoming' },
    completed:        { bg: '#F0FDF4', color: '#16a34a', border: '#86efac', label: 'Completed' },
    'checked-in':     { bg: '#eff6ff', color: '#1e40af', border: '#bfdbfe', label: 'Checked In' },
    cancelled:        { bg: '#fff1f2', color: '#be123c', border: '#fecdd3', label: 'Cancelled' },
    'refund_pending': { bg: '#FEF3C7', color: '#92400E', border: '#FCD34D', label: 'Refund Pending' },
    refunded:         { bg: '#D1FAE5', color: '#065F46', border: '#A7F3D0', label: 'Refunded' },
  };
  const s = map[status] || { bg: '#F8FAF7', color: '#374151', border: '#EEF2E6', label: status };
  return (
    <div style={{
      display: 'inline-block', background: s.bg, color: s.color,
      border: `1.5px solid ${s.border}`, borderRadius: '999px',
      padding: '4px 12px', fontSize: '0.72rem', fontWeight: 800,
      textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px',
    }}>
      {s.label}
    </div>
  );
}

function QRModal({ booking, token, onClose }) {
  const [qrUrl, setQrUrl] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const bookingId = booking._id || booking.id;

  useEffect(() => {
    let objectUrl = null;
    const loadQr = async () => {
      try {
        const res = await fetch(`${API}/bookings/${bookingId}/qr`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.msg || errData.detail || `Error ${res.status}`);
        }
        const contentType = res.headers.get('content-type') || '';
        if (!contentType.includes('image')) {
          throw new Error('Server did not return an image');
        }
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        setQrUrl(objectUrl);
      } catch (err) {
        console.error('Failed to load QR code:', err);
        setError(err.message || 'Failed to load QR code');
      } finally {
        setLoading(false);
      }
    };
    loadQr();
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [bookingId, token]);

  const shortId = booking.booking_id || bookingId?.slice(-8)?.toUpperCase() || '';

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '1rem',
    }} onClick={onClose}>
      <div style={{
        background: 'white', borderRadius: '20px', padding: '2rem',
        maxWidth: '400px', width: '100%', position: 'relative',
      }} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} style={{
          position: 'absolute', top: '1rem', right: '1rem',
          background: 'none', border: 'none', cursor: 'pointer',
        }}>
          <X size={24} color="#666" />
        </button>
        <h2 style={{ textAlign: 'center', marginBottom: '0.5rem', color: '#161616' }}>
          Booking QR Code
        </h2>
        <p style={{ textAlign: 'center', color: '#374151', marginBottom: '1.5rem' }}>
          Show this QR at the venue entrance
        </p>
        <div style={{
          display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
          background: '#F9FAFB', padding: '1rem', borderRadius: '12px',
          border: '2px solid #EEF2E6', minHeight: '250px',
        }}>
          {loading && (
            <div style={{ color: '#374151', fontWeight: 600 }}>Loading QR...</div>
          )}
          {!loading && qrUrl && (
            <img src={qrUrl} alt="Booking QR" style={{ width: '220px', height: '220px' }} />
          )}
          {!loading && !qrUrl && (
            <div style={{ textAlign: 'center', padding: '1rem' }}>
              {/* Fallback: show booking details as text QR placeholder */}
              <div style={{
                width: '180px', height: '180px', border: '3px solid #084734',
                borderRadius: '12px', display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem',
                background: 'white',
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📋</div>
                <div style={{ fontFamily: 'monospace', fontWeight: 900, fontSize: '1rem', color: '#084734' }}>
                  #{shortId}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#374151', marginTop: '4px', fontWeight: 600 }}>
                  BOOKING ID
                </div>
              </div>
              <div style={{ fontSize: '0.8rem', color: '#DC2626', fontWeight: 600 }}>
                {error || 'QR unavailable — show booking ID'}
              </div>
            </div>
          )}
        </div>
        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <p style={{ fontWeight: 700, color: '#161616', marginBottom: '4px' }}>
            {booking.turf_id?.name || 'Venue'}
          </p>
          <p style={{ fontSize: '0.8rem', color: '#374151', fontWeight: 600, marginBottom: '1rem' }}>
            Booking ID: <span style={{ fontFamily: 'monospace', color: '#084734' }}>#{shortId}</span>
          </p>
          <a
            href={`/verify-booking/${bookingId}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: '#084734', color: '#CEF17B', textDecoration: 'none',
              padding: '10px 18px', borderRadius: '12px', fontWeight: 800,
              fontSize: '0.85rem', boxShadow: '0 4px 14px rgba(8,71,52,0.2)'
            }}
          >
            🔗 Open Verification & Ticket Page
          </a>
        </div>
      </div>
    </div>
  );
}

function ActionBtn({ children, onClick, primary, danger }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', padding: '9px 14px', borderRadius: '10px', cursor: 'pointer',
        fontSize: '0.82rem', fontWeight: 800, transition: 'all 0.2s',
        border: primary ? 'none' : danger ? '1.5px solid #fecdd3' : '1.5px solid #EEF2E6',
        background: primary ? '#084734' : 'white',
        color: primary ? 'white' : danger ? '#be123c' : '#161616',
        boxShadow: primary ? '0 4px 12px rgba(8,71,52,0.2)' : 'none',
      }}
    >
      {children}
    </button>
  );
}

const labelStyle = {
  display: 'block', fontSize: '0.72rem', fontWeight: 800,
  color: '#94a3b8', marginBottom: '6px', textTransform: 'uppercase',
};
const inputStyle = {
  width: '100%', padding: '11px 14px', borderRadius: '10px',
  border: '1.5px solid #EEF2E6', fontSize: '0.9rem',
  fontWeight: 700, outline: 'none', boxSizing: 'border-box',
};

function BookingDetailsModal({ booking: b, onClose, handleDownloadInvoice: propDownloadInvoice, downloadingInvoiceId: propDownloadingId }) {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [internalDownloading, setInternalDownloading] = useState(false);

  const triggerDownload = async (bookingId, bookingNo) => {
    if (propDownloadInvoice) {
      return propDownloadInvoice(bookingId, bookingNo);
    }
    if (!token) return;
    setInternalDownloading(true);
    try {
      const res = await axios.get(`${API}/bookings/${bookingId}/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TurfX_Tax_Invoice_${bookingNo || bookingId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download invoice:', err);
      alert('Failed to download invoice PDF. Please try again.');
    } finally {
      setInternalDownloading(false);
    }
  };

  const isDownloading = propDownloadingId === (b._id || b.id || b.booking_id) || internalDownloading;
  const shortId = b.booking_id || b._id?.slice(-8)?.toUpperCase() || '';
  const timeStr = formatTimeSlot(Array.isArray(b.time_slots) ? b.time_slots : b.time_slot ? [b.time_slot] : []);
  const duration = getDuration(Array.isArray(b.time_slots) ? b.time_slots : b.time_slot ? [b.time_slot] : []);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '1rem', WebkitBackdropFilter: 'blur(4px)', backdropFilter: 'blur(4px)',
      animation: 'fadeIn 0.2s ease-out'
    }} onClick={onClose}>
      <div style={{
        background: 'white', borderRadius: '24px', padding: '2rem',
        maxWidth: '520px', width: '100%', position: 'relative',
        boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
        animation: 'slideUp 0.3s ease-out',
        border: '1.5px solid #EEF2E6',
        display: 'flex', flexDirection: 'column', gap: '1.25rem'
      }} onClick={e => e.stopPropagation()}>
        
        {/* Close Button */}
        <button onClick={onClose} style={{
          position: 'absolute', top: '1.25rem', right: '1.25rem',
          background: '#F8FAF7', border: 'none', cursor: 'pointer',
          width: '32px', height: '32px', borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background 0.2s', color: '#161616'
        }} onMouseEnter={e => e.currentTarget.style.background = '#EEF2E6'}
           onMouseLeave={e => e.currentTarget.style.background = '#F8FAF7'}>
          <X size={18} />
        </button>

        {/* Modal Header */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: '0.5rem' }}>
            <div>
              <span style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px' }}>Booking Summary</span>
              <h2 style={{ fontSize: '1.4rem', fontWeight: '900', color: '#161616', margin: '4px 0 0' }}>
                #{shortId}
              </h2>
            </div>
            <div style={{ transform: 'scale(0.95)', transformOrigin: 'top right' }}>
              <StatusBadge status={b.status} />
            </div>
          </div>
        </div>

        {/* Venue details */}
        <div style={{ background: '#F8FAF7', border: '1.5px solid #EEF2E6', borderRadius: '16px', padding: '1.2rem', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ fontSize: '1.1rem', fontWeight: '800', color: '#084734' }}>{b.turf_id?.name || b.turf_name || 'Venue'}</div>
          <div style={{ fontSize: '0.82rem', color: '#374151', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
            📍 {b.turf_id?.location || b.turf_location}{b.turf_id?.city || b.turf_city ? `, ${b.turf_id?.city || b.turf_city}` : ''}
          </div>
          <div style={{ fontSize: '0.82rem', color: '#374151', fontWeight: '700' }}>
            🏆 Sport: <span style={{ textTransform: 'capitalize' }}>{b.sport || b.turf_id?.sport || 'General'}</span>
          </div>
        </div>

        {/* Timing info */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <div style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: '800', textTransform: 'uppercase' }}>Date</div>
            <div style={{ fontWeight: '800', color: '#161616', fontSize: '0.9rem', marginTop: '4px' }}>
              📅 {formatDate(b.date)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: '800', textTransform: 'uppercase' }}>Time Slot</div>
            <div style={{ fontWeight: '800', color: '#161616', fontSize: '0.9rem', marginTop: '4px' }}>
              🕒 {timeStr}
            </div>
          </div>
        </div>

        {/* Financial Details (Amount Paid) */}
        <div style={{ borderTop: '1.5px dashed #EEF2E6', paddingTop: '1rem' }}>
          <h4 style={{ margin: '0 0 10px', fontSize: '0.8rem', fontWeight: '800', color: '#161616', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Payment Summary
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#374151', fontWeight: '600' }}>
              <span>Base Court Fee ({duration})</span>
              <span>₹{b.base_amount || b.court_amount || 0}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#374151', fontWeight: '600' }}>
              <span>Platform Convenience Fee</span>
              <span>₹{b.user_platform_fee || b.platform_fee || 0}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#374151', fontWeight: '600', borderBottom: '1px solid #EEF2E6', paddingBottom: '8px' }}>
              <span>GST</span>
              <span>₹{b.user_gst || b.gst || 0}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.05rem', color: '#084734', fontWeight: '900', marginTop: '4px' }}>
              <span>Total Amount Paid</span>
              <span>₹{b.total_price || 0}</span>
            </div>
          </div>
        </div>

        {/* Cancellation Details if Cancelled */}
        {b.status === 'cancelled' && (
          <div style={{ background: '#FFF1F2', border: '1.5px solid #FECDD3', borderRadius: '16px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: '800', color: '#BE123C', textTransform: 'uppercase' }}>Cancellation Info</div>
            <div style={{ fontSize: '0.85rem', color: '#9F1239', fontWeight: '600' }}>
              <strong>Refunded to Wallet:</strong> ₹{b.refund_amount || 0}
            </div>
            {b.cancel_reason && (
              <div style={{ fontSize: '0.82rem', color: '#9F1239', fontWeight: '500' }}>
                <strong>Reason:</strong> {b.cancel_reason}
              </div>
            )}
          </div>
        )}

        {/* Action button inside details */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '0.5rem' }}>
          <button 
            onClick={() => triggerDownload(b._id || b.id || b.booking_id, b.booking_id || b._id)}
            disabled={isDownloading}
            style={{
              width: '100%', padding: '12px', borderRadius: '12px',
              border: '1.5px solid #084734', background: '#F0FDF4',
              color: '#084734', fontWeight: '850', fontSize: '0.9rem',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
            }}
          >
            📄 {isDownloading ? 'Generating Invoice PDF...' : 'Download Official Tax Invoice'}
          </button>
          
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              onClick={() => {
                onClose();
                navigate(`/turf/${b.turf_id?._id || b.turf_id}`);
              }}
              style={{
                flex: 1, padding: '12px', borderRadius: '12px', border: 'none',
                background: '#084734', color: 'white', fontWeight: '800',
                fontSize: '0.9rem', cursor: 'pointer', textAlign: 'center',
                boxShadow: '0 4px 12px rgba(8,71,52,0.2)'
              }}
            >
              View Venue Details
            </button>
            <button 
              onClick={onClose}
              style={{
                padding: '12px 20px', borderRadius: '12px',
                border: '1.5px solid #EEF2E6', background: 'white',
                color: '#374151', fontWeight: '700', fontSize: '0.9rem',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>

      </div>
      
      {/* Modal specific keyframe animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

